import { FunctionComponent, ReactNode, useMemo } from 'react'
import { css, cx } from '@emotion/css'
import { Theme, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import Big from 'big.js'
import { useTranslation } from 'react-i18next'
import PulsateLoader from '@/components/PulsateLoader'
import type { OperationalMenu } from '@/libs/helpers/adapters'
import { OrderItemDiscount } from '@/libs/helpers/apiClient'
import { useMenuItem } from '@/libs/helpers/hooks/useMenuItem'
import {
  OrderChildItem,
  OrderItem,
  calcDiscountTotal,
  calcLineItemCost,
  flattenChildItems,
  unravelToOneQuantity,
} from '@/libs/helpers/utils'
import { Button, SvgIcon, Text } from '@/tbui'
import { ItemDiscounts } from '../ItemDiscounts'

const QUANTITY_MAX_ITEMS = 99

export enum CartWidgetVariant {
  CHECKOUT = 'CHECKOUT',
  MENU = 'MENU',
}

const cartItemStyles = css`
  display: grid;
  gap: 1rem;
`

const cartTotalStyles = css`
  display: grid;
  grid-template-columns: auto 1fr;
`

const cartItemTile = (theme: Theme): string => css`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-content: center;
  align-items: center;
  gap: 20px;
  border-radius: ${theme.shape.RADIUS};
  background: ${theme.palette.WHITE};
  word-break: break-word;
  grid-template-columns: 1fr auto;
`

const counterStyles = css`
  display: grid;
  grid-template-columns: repeat(3, auto);
  align-content: center;
  align-items: center;
  gap: 8px;
`

const itemNoteStyles = css`
  display: grid;
  grid-template-columns: auto auto;
  gap: 10px;
`

const StyledButton = styled.button`
  font-family: ${(props) => props.theme.font.FAMILY_BOLD};
  background: ${(props) => props.theme.palette.WHITE};
  color: ${(props) => props.theme.palette.PRIMARY};
  place-content: center;
  border-radius: 6px;
  font-size: 14px;
  display: grid;
  height: 44px;
  border: none;
  width: 44px;
  background: ${(props) => props.theme.palette.SECONDARY};
  :disabled {
    filter: grayscale(100%);
    pointer-events: none;
  }
`

const StyledQuantityText = styled(Text)`
  width: 20px;
  text-align: center;
`

const StyledNotes = styled(Text)`
  word-break: break-all;
`

const StyledModifierWrapper = styled.div`
  display: grid;
  gap: 10px;
`

const modifierStyles = css`
  display: grid;
  grid-template-columns: auto auto;
  gap: 20px;
`

const modifierTextStyles = css`
  display: flex;
  gap: 6px;
`

const StylePrice = styled(Text)`
  word-break: normal;
`

interface CartItemProps {
  item: OrderItem
  index: number
  isCartLoading: boolean
  operationalMenu: OperationalMenu
  onDecrementItem: (item: OrderItem, index: number) => Promise<void>
  onIncrementItem: (item: OrderItem, index: number) => Promise<void>
  onRemoveItem: (item: OrderItem) => void
  orderItemDiscounts: OrderItemDiscount[]
}

const CartItem: FunctionComponent<CartItemProps> = ({
  item,
  index,
  isCartLoading,
  operationalMenu,
  onIncrementItem,
  onDecrementItem,
  onRemoveItem,
  orderItemDiscounts,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { setMenuItem, setItemIndex } = useMenuItem()
  const menuItems = operationalMenu.menuItems

  const { name: itemName, childItems, quantity, note, itemID, menuGroupXRefID } = item
  const quantityAsNumber = Number(quantity)
  const itemSubtotal = calcLineItemCost(item)
  const discountTotal = calcDiscountTotal(orderItemDiscounts)
  const itemTotal = Big(itemSubtotal).sub(discountTotal).toFixed(2)

  const renderCounters: ReactNode = (
    <div className={cartTotalStyles}>
      <div className={counterStyles}>
        <StyledButton
          data-test={`cart-item-${item.itemID}-${index}-minus`}
          onClick={() => onDecrementItem(item, index)}
          disabled={isCartLoading}
        >
          <SvgIcon name="remove" color="TEXT_SECONDARY" fontSize="36px" />
        </StyledButton>
        <StyledQuantityText data-test={`cart-item-${item.itemID}-${index}-quantity`} bold>
          {quantityAsNumber}
        </StyledQuantityText>
        <StyledButton
          disabled={quantityAsNumber === QUANTITY_MAX_ITEMS || isCartLoading}
          onClick={() => onIncrementItem(item, index)}
          data-test={`cart-item-${item.itemID}-${index}-plus`}
        >
          <SvgIcon name="add" color="TEXT_SECONDARY" fontSize="30px" />
        </StyledButton>
      </div>
      <div css={{ justifySelf: 'end', alignSelf: 'center' }}>
        <StylePrice
          type="H4"
          transform="capitalize"
          data-test={`cart-item-${item.itemID}-${index}-total-price`}
        >
          {t('currency', {
            value: itemTotal,
          })}
        </StylePrice>
      </div>
    </div>
  )

  const handleItemClick = (): void => {
    const menuItem = menuItems[itemID]
    setItemIndex(index)
    setMenuItem({ ...menuItem, menuGroupXRefID })
  }

  const renderModifiers = (
    {
      modifierID,
      modifierName,
      modifierQuantity,
      modifierPrice,
      modifierGroupXRefID,
      depth,
    }: {
      modifierID: string
      modifierName: string
      modifierQuantity: string
      modifierPrice: string
      modifierGroupXRefID: string
      depth: number
    },
    idx: number
  ): ReactNode => {
    return (
      <div
        className={modifierStyles}
        data-test={`cart-item-modifier-${modifierGroupXRefID}_${modifierID}-${index}`}
        key={`${modifierGroupXRefID}_${modifierID}_${index}_${idx}`}
      >
        <div className={modifierTextStyles}>
          <Text
            transform="capitalize"
            style={{ marginLeft: `${depth * 8}px` }}
          >{`+ ${modifierName}`}</Text>
          {modifierQuantity !== '1.00' && (
            <Text> {`(x${Math.floor(Number(modifierQuantity))})`}</Text>
          )}
        </div>
        {isCartLoading ? (
          <PulsateLoader />
        ) : (
          <Text transform="capitalize" align="right">
            {`${t('currency', {
              value: modifierPrice,
            })}`}
          </Text>
        )}
      </div>
    )
  }

  const flattenedChildItems: (OrderChildItem & { depth: number })[] = useMemo(
    () =>
      childItems && childItems.length > 0
        ? flattenChildItems(unravelToOneQuantity(childItems), 0)
        : [],
    [childItems]
  )

  return (
    <div data-test={`cart-item-${item.itemID}-${index}`} className={cartItemStyles}>
      <div css={{ display: 'grid', gap: '0.5rem' }}>
        <div className={cx(cartItemTile(theme))}>
          <Text
            onClick={handleItemClick}
            transform="capitalize"
            type="H4"
            data-test={`cart-item-${item.itemID}-${index}-name`}
          >
            {itemName}
          </Text>
          {isCartLoading ? (
            <PulsateLoader height="16px" />
          ) : (
            <StylePrice
              type="H4"
              bold={false}
              transform="capitalize"
              data-test={`cart-item-${item.itemID}-${index}-price`}
            >
              {t('currency', {
                value: item.price,
              })}
            </StylePrice>
          )}
        </div>
        {((flattenedChildItems && flattenedChildItems.length > 0) || note) && (
          <StyledModifierWrapper>
            {(flattenedChildItems ?? []).map(
              (
                {
                  itemID: modifierID,
                  price: modifierPrice,
                  name: modifierName,
                  quantity: modifierQuantity,
                  modifierGroupXRefID,
                  depth,
                },
                idx
              ) =>
                renderModifiers(
                  {
                    modifierID,
                    modifierPrice,
                    modifierName,
                    modifierQuantity,
                    modifierGroupXRefID,
                    depth,
                  },
                  idx
                )
            )}
            {note && (
              <div data-test={`cart-item-${item.itemID}-${index}-notes`} className={itemNoteStyles}>
                <StyledNotes transform="capitalize">
                  + {t('cart_widget.item.notes', { value: note })}
                </StyledNotes>
              </div>
            )}
          </StyledModifierWrapper>
        )}
      </div>

      {!isCartLoading && (
        <ItemDiscounts
          index={index}
          itemTotal={itemSubtotal}
          orderItemDiscounts={orderItemDiscounts}
        />
      )}
      {renderCounters}
      <Button
        css={{ display: 'flex', width: 'max-content' }}
        variant="LINK"
        onClick={() => onRemoveItem(item)}
      >
        {t('modal.remove_item_confirm.submit')}
      </Button>
    </div>
  )
}

export default CartItem
